import { AuditedProduct, SCORING_CONFIG, VIDEO_SCORING_ENABLED } from './scoring-engine-constants';
import { TITLE_CHAR_LIMIT } from './title-compliance-constants';

// Title bottlenecks are derived from the Portfolio Health title band rather
// than hard-coded, so the bottleneck list and the score can never drift apart:
// a title only shows up as a bottleneck if it actually loses title points.
const TITLE_PASS_MIN = SCORING_CONFIG.title.passMin;

export const DeficiencySeverity = {
  WARNING: 'warning',
  ERROR: 'error'
} as const;
export type DeficiencySeverityType = (typeof DeficiencySeverity)[keyof typeof DeficiencySeverity];

export interface DeficiencyFilter {
  id: string;
  label: string;
  /** Concise name shown on the bottleneck row and the table filter chip. */
  shortLabel: string;
  problemStatement: string;
  /** Muted annotation making the check self-explanatory, e.g. '0 videos', '< 801 chars'. */
  thresholdText: string;
  countPredicate: (p: AuditedProduct) => boolean;
  severity: DeficiencySeverityType;
  remedyText: string;
}

const ALL_DEFICIENCIES: DeficiencyFilter[] = [
  {
    id: 'reviews_less_100',
    label: 'Reviews Deficit (< 100 Reviews)',
    shortLabel: 'Low review count',
    problemStatement: 'Products have less than 100 customer reviews',
    thresholdText: '< 100 reviews',
    countPredicate: p => p.totalReviews < 100,
    severity: DeficiencySeverity.ERROR,
    remedyText: 'Accelerate reviews via VINE or customer enrollment email flows.'
  },
  {
    id: 'aplus_missing',
    label: 'Missing A+ Enhanced Content',
    shortLabel: 'Missing A+ content',
    problemStatement: 'Products with no active A+ rich descriptions',
    thresholdText: 'no active modules',
    countPredicate: p => !p.aplusPresent,
    severity: DeficiencySeverity.ERROR,
    remedyText: 'Convert standard product description into structured modules.'
  },
  {
    id: 'bullets_less_5',
    label: 'Inadequate Bullets (< 5)',
    shortLabel: 'Too few bullets',
    problemStatement: 'Products with less than 5 key features text lines',
    thresholdText: '< 5 bullets',
    countPredicate: p => p.featureBulletsCount < 5,
    severity: DeficiencySeverity.WARNING,
    remedyText: 'Write detailed bullets utilizing core keywords & customer benefit hooks.'
  },
  {
    id: 'title_short',
    label: `Short Product Title (< ${TITLE_PASS_MIN} Chars)`,
    shortLabel: 'Short title',
    problemStatement: `Product titles containing fewer than ${TITLE_PASS_MIN} characters`,
    thresholdText: `< ${TITLE_PASS_MIN} chars`,
    countPredicate: p => p.title.length < TITLE_PASS_MIN,
    severity: DeficiencySeverity.WARNING,
    remedyText: `Expand titles toward the ${TITLE_CHAR_LIMIT}-char cap with Brand, Type, Material, Pack size & Key benefit.`
  },
  {
    // Over the cap is the hardest title failure there is — Amazon truncates the
    // listing and the title scores 0, not partial credit — so it is an ERROR,
    // and a title cannot be both over the cap and 'short'.
    id: 'title_over_cap',
    label: `Title Over Amazon's Cap (> ${TITLE_CHAR_LIMIT} Chars)`,
    shortLabel: 'Title over cap',
    problemStatement: `Product titles longer than Amazon's ${TITLE_CHAR_LIMIT}-character cap`,
    thresholdText: `> ${TITLE_CHAR_LIMIT} chars`,
    countPredicate: p => p.title.length > TITLE_CHAR_LIMIT,
    severity: DeficiencySeverity.ERROR,
    remedyText: `Trim to ${TITLE_CHAR_LIMIT} chars via Title Compliance before Amazon truncates or suppresses the listing.`
  },
  {
    id: 'images_less_5',
    label: 'Low Image Count (< 5 Images)',
    shortLabel: 'Low image count',
    problemStatement: 'Listing detail pages with less than 5 secondary images',
    thresholdText: '< 5 images',
    countPredicate: p => p.countOfImages < 5,
    severity: DeficiencySeverity.ERROR,
    remedyText: 'Introduce packaging assets, lifestyle scenes, and size charts.'
  },
  {
    id: 'no_videos',
    label: 'Missing Video Assets (0 Videos)',
    shortLabel: 'Missing video assets',
    problemStatement: 'Product listings with no visual/video uploads',
    thresholdText: '0 videos',
    countPredicate: p => p.videosCount === 0,
    severity: DeficiencySeverity.ERROR,
    remedyText: 'Upload a brief 30-sec assembly, product benefit, or unboxing video.'
  },
  {
    id: 'desc_short',
    label: 'Deficient Description (< 801 Chars)',
    shortLabel: 'Short description',
    problemStatement: 'Product descriptions shorter than 801 characters',
    thresholdText: '< 801 chars',
    countPredicate: p => p.fullDescription.length < 801,
    severity: DeficiencySeverity.WARNING,
    remedyText: 'Add product specifications, usage instructions, or FAQ details.'
  },
  {
    id: 'low_ratings',
    label: 'Rating Deficit (< 4.2 Stars)',
    shortLabel: 'Low star rating',
    problemStatement: 'Products holding poor client rating averages under 4.2',
    thresholdText: '< 4.2 stars',
    countPredicate: p => p.rating > 0 && p.rating < 4.2,
    severity: DeficiencySeverity.ERROR,
    remedyText: 'Identify returns patterns in catalog voice or address production defects.'
  },
  {
    id: 'missing_rating',
    label: 'Missing Rating Data',
    shortLabel: 'No rating captured',
    problemStatement: 'Listings with no star rating captured by the scraper',
    thresholdText: 'scraper returned none',
    countPredicate: p => p.rating === 0,
    severity: DeficiencySeverity.WARNING,
    remedyText: 'Confirm scraper coverage; new listings may not yet have received reviews.'
  }
];

// The `no_videos` check is excluded while video scoring is disabled (the scraper
// does not return video data, so it would flag every ASIN). Flip
// VIDEO_SCORING_ENABLED to restore it. See scoring-engine-constants.ts.
export const DEFICIENCIES_LIST: DeficiencyFilter[] = ALL_DEFICIENCIES.filter(
  d => VIDEO_SCORING_ENABLED || d.id !== 'no_videos'
);

export interface DeficiencyStat {
  filter: DeficiencyFilter;
  count: number;
  /** Product ids (AuditedProduct.id) failing this check. */
  affectedIds: Set<string>;
}

export interface DeficiencySummary {
  /** Checks with >=1 failing listing, sorted by impact (count desc, errors before warnings). */
  flagged: DeficiencyStat[];
  /** Checks every listing passes. */
  passing: DeficiencyFilter[];
  /** Total failing (check x listing) pairs — the 'issue instances' figure. */
  issueInstances: number;
  /** Distinct listings failing at least one check. */
  affectedListings: number;
  totalChecks: number;
}

/**
 * Single source of truth for bottleneck counts. Consumed by the DeficienciesPanel
 * and by the table's filter-chip bar so the two never drift.
 */
export const computeDeficiencySummary = (products: AuditedProduct[]): DeficiencySummary => {
  const flagged: DeficiencyStat[] = [];
  const passing: DeficiencyFilter[] = [];
  const affectedSet = new Set<string>();
  let issueInstances = 0;

  DEFICIENCIES_LIST.forEach(filter => {
    const matches = products.filter(filter.countPredicate);
    if (matches.length === 0) {
      passing.push(filter);
      return;
    }
    const affectedIds = new Set(matches.map(p => p.id));
    matches.forEach(p => affectedSet.add(p.id));
    issueInstances += matches.length;
    flagged.push({ filter, count: matches.length, affectedIds });
  });

  const severityRank = (s: DeficiencySeverityType) => (s === DeficiencySeverity.ERROR ? 0 : 1);
  flagged.sort((a, b) =>
    b.count !== a.count
      ? b.count - a.count
      : severityRank(a.filter.severity) - severityRank(b.filter.severity)
  );

  return {
    flagged,
    passing,
    issueInstances,
    affectedListings: affectedSet.size,
    totalChecks: DEFICIENCIES_LIST.length
  };
};
